import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Calendar, 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  Users,
  CalendarPlus,
  Send
} from 'lucide-react';
import { format } from 'date-fns';

interface ActivityItem {
  id: string;
  type: 'event_created' | 'approval_requested' | 'approval_approved' | 'approval_rejected' | 'feedback_submitted' | 'feedback_responded';
  title: string;
  description: string;
  timestamp: string;
  clientId: string;
  clientName: string;
  eventName?: string;
  metadata?: Record<string, any>;
}

interface Client {
  id: string;
  name: string;
}

export function ClientActivityTimeline() {
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadClientsAndActivities();
    }
  }, [user]);

  const loadClientsAndActivities = async () => {
    if (!user) return;
    setLoading(true);

    try {
      // Load clients
      const { data: clientsData } = await supabase
        .from('clients')
        .select('id, name')
        .eq('user_id', user.id);

      setClients(clientsData || []);

      if (!clientsData || clientsData.length === 0) {
        setActivities([]);
        setLoading(false);
        return;
      }

      const clientIds = clientsData.map(c => c.id);
      const clientMap = new Map(clientsData.map(c => [c.id, c.name]));

      // Load events with client assignments
      const { data: eventsData } = await supabase
        .from('events')
        .select('id, name, created_at, client_id')
        .eq('user_id', user.id)
        .in('client_id', clientIds);

      // Load approvals
      const { data: approvalsData } = await supabase
        .from('event_approvals')
        .select('id, event_id, client_id, status, requested_at, approved_at, approval_notes')
        .in('client_id', clientIds);

      // Load feedback
      const { data: feedbackData } = await supabase
        .from('client_feedback')
        .select('id, event_id, client_id, feedback_type, message, status, response_message, created_at, responded_at')
        .in('client_id', clientIds);

      // Get event names for approvals and feedback
      const eventIds = [
        ...(approvalsData || []).map(a => a.event_id),
        ...(feedbackData || []).map(f => f.event_id)
      ].filter(Boolean);

      const { data: eventNames } = await supabase
        .from('events')
        .select('id, name')
        .in('id', eventIds);

      const eventNameMap = new Map((eventNames || []).map(e => [e.id, e.name]));

      // Build activity items
      const activityItems: ActivityItem[] = [];

      // Events created with client assignment
      (eventsData || []).forEach(event => {
        if (event.client_id) {
          activityItems.push({
            id: `event-${event.id}`,
            type: 'event_created',
            title: 'Event Created',
            description: `Event "${event.name}" was created and assigned to client`,
            timestamp: event.created_at,
            clientId: event.client_id,
            clientName: clientMap.get(event.client_id) || 'Unknown',
            eventName: event.name
          });
        }
      });

      // Approvals
      (approvalsData || []).forEach(approval => {
        // Approval requested
        activityItems.push({
          id: `approval-req-${approval.id}`,
          type: 'approval_requested',
          title: 'Approval Requested',
          description: `Approval requested for event "${eventNameMap.get(approval.event_id) || 'Unknown'}"`,
          timestamp: approval.requested_at,
          clientId: approval.client_id,
          clientName: clientMap.get(approval.client_id) || 'Unknown',
          eventName: eventNameMap.get(approval.event_id)
        });

        // Approval response
        if (approval.approved_at && approval.status !== 'pending') {
          activityItems.push({
            id: `approval-resp-${approval.id}`,
            type: approval.status === 'approved' ? 'approval_approved' : 'approval_rejected',
            title: approval.status === 'approved' ? 'Approval Granted' : 'Approval Rejected',
            description: approval.approval_notes || `Event "${eventNameMap.get(approval.event_id) || 'Unknown'}" was ${approval.status}`,
            timestamp: approval.approved_at,
            clientId: approval.client_id,
            clientName: clientMap.get(approval.client_id) || 'Unknown',
            eventName: eventNameMap.get(approval.event_id)
          });
        }
      });

      // Feedback
      (feedbackData || []).forEach(feedback => {
        // Feedback submitted
        activityItems.push({
          id: `feedback-sub-${feedback.id}`,
          type: 'feedback_submitted',
          title: 'Feedback Submitted',
          description: feedback.message.substring(0, 100) + (feedback.message.length > 100 ? '...' : ''),
          timestamp: feedback.created_at,
          clientId: feedback.client_id,
          clientName: clientMap.get(feedback.client_id) || 'Unknown',
          eventName: eventNameMap.get(feedback.event_id),
          metadata: { feedbackType: feedback.feedback_type }
        });

        // Feedback response
        if (feedback.responded_at && feedback.response_message) {
          activityItems.push({
            id: `feedback-resp-${feedback.id}`,
            type: 'feedback_responded',
            title: 'Feedback Response Sent',
            description: feedback.response_message.substring(0, 100) + (feedback.response_message.length > 100 ? '...' : ''),
            timestamp: feedback.responded_at,
            clientId: feedback.client_id,
            clientName: clientMap.get(feedback.client_id) || 'Unknown',
            eventName: eventNameMap.get(feedback.event_id)
          });
        }
      });

      // Sort by timestamp descending
      activityItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setActivities(activityItems);
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'event_created':
        return <CalendarPlus className="h-4 w-4" />;
      case 'approval_requested':
        return <Clock className="h-4 w-4" />;
      case 'approval_approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'approval_rejected':
        return <XCircle className="h-4 w-4" />;
      case 'feedback_submitted':
        return <MessageSquare className="h-4 w-4" />;
      case 'feedback_responded':
        return <Send className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getActivityColor = (type: ActivityItem['type']) => {
    switch (type) {
      case 'event_created':
        return 'bg-blue-500';
      case 'approval_requested':
        return 'bg-yellow-500';
      case 'approval_approved':
        return 'bg-green-500';
      case 'approval_rejected':
        return 'bg-red-500';
      case 'feedback_submitted':
        return 'bg-purple-500';
      case 'feedback_responded':
        return 'bg-teal-500';
      default:
        return 'bg-muted';
    }
  };

  const filteredActivities = selectedClient === 'all' 
    ? activities 
    : activities.filter(a => a.clientId === selectedClient);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Client Activity Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Client Activity Timeline
          </CardTitle>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Filter by client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map(client => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {filteredActivities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No client activity yet</p>
            <p className="text-sm">Activity will appear here when you interact with clients</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[15px] top-0 bottom-0 w-0.5 bg-border" />
              
              <div className="space-y-4">
                {filteredActivities.map((activity) => (
                  <div key={activity.id} className="relative pl-10">
                    {/* Timeline dot */}
                    <div className={`absolute left-0 top-1 w-8 h-8 rounded-full ${getActivityColor(activity.type)} flex items-center justify-center text-white`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    
                    <div className="bg-muted/50 rounded-lg p-3">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                        <h4 className="font-medium text-sm">{activity.title}</h4>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(activity.timestamp), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{activity.description}</p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="text-xs">
                          {activity.clientName}
                        </Badge>
                        {activity.eventName && (
                          <Badge variant="secondary" className="text-xs">
                            {activity.eventName}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
